import { Word } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import { Identified, Named, PotentiallyDescribed } from '../../interfaces'

/**
 * Represents an enumeration of values that can be selected
 *
 * Used as a single select or multiselect
 */
export class Enum {
  /**
   * Acceptable values for this {@link Enum}
   *
   * @see {@link Entry}
   */
  public readonly entries: readonly Enum.Entry[]

  /**
   * If `true`, multiple entries can be selected at once. If `false`, only
   * one entry can be selected at a time.
   */
  public readonly isMultiselect: boolean

  constructor({
    entries,
    isMultiselect = false,
  }: Readonly<Enum.constructor.Params>) {
    this.entries = entries
    this.isMultiselect = isMultiselect
  }

  /**
   * Checks if the given value is a valid value for this {@link Enum}
   *
   * Does so by seeing if there is any valid {@link Enum.Entry} corresponding
   * to the given value, using {@link lookupEntry}
   */
  public hasEntry(value: unknown): boolean {
    return this.lookupEntry(value) === undefined
  }

  /**
   * Finds the {@link Enum.Entry} that best matches the given value
   *
   * Does so by trying to find an {@link Enum.Entry} with an ID that matches
   * the given value. If that fails, it tries to find one with a name that
   * matches the given value.
   */
  public lookupEntry(value: unknown): Enum.Entry | undefined {
    let id: string | undefined, name: string | undefined
    if (typeof value === 'string') {
      // If we receive a string, we can see if it matches any ID or name
      id = value
      name = value
    } else if (TypeChecker.isObject(value)) {
      // If we receive an object, we assume it is an entry object and we just
      // try to match by ID
      id = (value as Partial<Enum.Entry>)?.id
    }
    if (id === undefined && name === undefined) {
      // If we have no ID or name to try and match, then we can't find a
      // corresponding entry
      return undefined
    }
    return (
      // Looks up by ID first
      this.entries.find(entry => entry.id === id) ??
      // If that fails, looks up by name
      this.entries.find(entry => entry.name === name)
    )
  }
}
export namespace Enum {
  export namespace constructor {
    export interface Params {
      /** @see {@link Enum.entries} */
      entries: readonly Entry[]
      /**
       * @default false
       * @see {@link Enum.isMultiselect}
       */
      isMultiselect?: boolean
    }
    export namespace Params {
      export const isParams: TypeChecker<Params> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.object({
            entries: TypeChecker.Factory.list(Entry.isEntry),
            isMultiselect: TypeChecker.isBoolean,
          }),
      )
    }
  }

  export const isEnum: TypeChecker<Enum> = TypeChecker.Factory.instance(
    Enum,
  ).usingAlias('common.value.types.Enum')

  /**
   * An allowed value in an {@link Enum}
   */
  export type Entry = Readonly<
    Identified<Entry.Id> & Named & PotentiallyDescribed
  >
  export namespace Entry {
    export const isEntry: TypeChecker<Entry> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        Identified.getTypeChecker(Id.isId),
        Named.isNamed,
        PotentiallyDescribed.isPotentiallyDescribed,
      ),
    )

    /**
     * Identifies an {@link Entry}
     */
    export type Id = string
    export namespace Id {
      export const isId: TypeChecker<Id> = TypeChecker.isString.usingAlias(
        'common.value.types.Enum.Entry.Id',
      )

      /**
       * Generates an {@link Enum.Entry.Id}
       */
      export function generate(): Id {
        return Word.generate()
      }
    }
  }
}
